import { toPosAmount } from '@/components/pos/posCatalog'

/** @typedef {import('@/types/api').Sale} Sale */
/** @typedef {import('@/types/api').SaleLine} SaleLine */

/**
 * @param {Sale | null | undefined} sale
 */
export function getParentSaleLines(sale) {
  return (sale?.lines ?? []).filter((line) => !line.parent_line_id)
}

/**
 * POS cart display order — newest lines first (matches restaurant ERP bill grid).
 * @param {SaleLine[]} lines
 */
export function sortParentSaleLinesNewestFirst(lines) {
  return [...lines].sort((left, right) => {
    const sortDiff = (right.sort ?? 0) - (left.sort ?? 0)
    if (sortDiff !== 0) {
      return sortDiff
    }
    return (right.id ?? 0) - (left.id ?? 0)
  })
}

/**
 * @param {Sale | null | undefined} sale
 */
export function getParentSaleLinesForDisplay(sale) {
  return sortParentSaleLinesNewestFirst(getParentSaleLines(sale))
}

/**
 * @param {SaleLine[]} lines Backend sort order (oldest first).
 */
export function getNewestParentLineId(lines) {
  if (!lines.length) {
    return null
  }
  return lines[lines.length - 1]?.id ?? null
}

/**
 * @param {Sale | null | undefined} sale
 * @param {number} parentLineId
 */
export function getChildSaleLines(sale, parentLineId) {
  const parent = sale?.lines?.find((line) => line.id === parentLineId)
  if (parent?.child_lines?.length) {
    return parent.child_lines
  }
  return (sale?.lines ?? []).filter((line) => line.parent_line_id === parentLineId)
}

/**
 * @param {Sale | null | undefined} sale
 */
export function toPosTotals(sale) {
  const total = toPosAmount(sale?.total)
  return {
    subtotal: toPosAmount(sale?.subtotal),
    discount: toPosAmount(sale?.discount_amount),
    vat: toPosAmount(sale?.tax_amount),
    tip: toPosAmount(sale?.tip_amount),
    grand_total: total,
    balance_due: total,
  }
}

/**
 * @param {Sale | null | undefined} sale
 * @param {string} [branchLabel]
 */
export function toSaleHeaderMeta(sale, branchLabel = 'Branch') {
  const openedAt = sale?.created_at
    ? new Date(sale.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '--:--'

  return {
    sale_number: sale?.sale_number ?? 'New ticket',
    branch_label: branchLabel,
    opened_at: openedAt,
    status: sale?.status ?? 'draft',
  }
}

/**
 * @param {SaleLine[]} lines
 */
export function countServiceMinutes(lines) {
  return lines.reduce((sum, line) => sum + (line.duration ?? 0) * (line.quantity ?? 1), 0)
}

/**
 * @param {import('@/types/api').ServiceAddonOption[]} options
 * @param {SaleLine[]} attachedAddons
 */
export function getAvailableOptionalAddons(options, attachedAddons) {
  const attachedIds = new Set(attachedAddons.map((line) => line.service_id))
  return options.filter((option) => !option.required && !attachedIds.has(option.addon_service_id))
}

/**
 * @param {number | string | null | undefined} value
 */
export function normalizeSplitPercent(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 100
  return Math.min(100, Math.max(0, parsed))
}
